import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation34({ animation, badge, all_animations }: Props) {
  const [sparking, setSparking] = useState(false)
  const [showBadge, setShowBadge] = useState(false)
  const [sparkIndex, setSparkIndex] = useState(0)

  useEffect(() => {
    setTimeout(() => setSparking(true), 200)

    // Créer des étincelles successives
    const sparkInterval = setInterval(() => {
      setSparkIndex(prev => prev + 1)
    }, 100)

    setTimeout(() => {
      clearInterval(sparkInterval)
      setShowBadge(true)
    }, 1500)

    return () => clearInterval(sparkInterval)
  }, [])

  const sparks = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    angle: Math.random() * 360,
    distance: 50 + Math.random() * 100,
    size: 2 + Math.random() * 4
  }))

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes spark {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(var(--tx), var(--ty)) scale(0);
            opacity: 0;
          }
        }
        @keyframes electricGlow {
          0%, 100% {
            filter: drop-shadow(0 0 5px #FFF198);
          }
          50% {
            filter: drop-shadow(0 0 20px #FFF198) drop-shadow(0 0 40px #FFF198);
          }
        }
        @keyframes zap {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
        .spark {
          animation: spark 0.5s ease-out forwards;
        }
        .electric-glow {
          animation: electricGlow 0.2s ease-in-out 5;
        }
        .zap {
          animation: zap 0.1s ease-in-out infinite;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        <div className="relative">
          {/* Éclairs */}
          {sparking && !showBadge && (
            <>
              <svg className="absolute -top-8 -left-8 w-16 h-16 text-tessan-yellow zap" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
              <svg className="absolute -top-8 -right-8 w-16 h-16 text-tessan-yellow zap" style={{ animationDelay: '0.05s' }} viewBox="0 0 24 24" fill="currentColor">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </>
          )}

          {/* Étincelles */}
          {sparking && sparks.map(spark => (
            sparkIndex > spark.id && (
              <div
                key={spark.id}
                className="absolute left-1/2 top-1/2 rounded-full bg-tessan-yellow spark"
                style={{
                  '--tx': `${Math.cos((spark.angle * Math.PI) / 180) * spark.distance}px`,
                  '--ty': `${Math.sin((spark.angle * Math.PI) / 180) * spark.distance}px`,
                  width: `${spark.size}px`,
                  height: `${spark.size}px`,
                  marginLeft: `-${spark.size / 2}px`,
                  marginTop: `-${spark.size / 2}px`
                } as React.CSSProperties}
              />
            )
          ))}

          {/* Badge */}
          <div className={`relative z-10 transition-all duration-300 ${showBadge ? 'opacity-100 scale-100 electric-glow' : 'opacity-0 scale-75'}`}>
            <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
              <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
            </div>
          </div>

          {/* Centre d'énergie initial */}
          {sparking && !showBadge && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 rounded-full bg-tessan-yellow animate-ping" />
            </div>
          )}
        </div>

        <div className={`mt-8 text-center transition-all duration-700 ${showBadge ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
